import React, { useMemo, useRef, useEffect } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { ButtonColor } from '@/components/adg4/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { NavProductRecommendationsContext } from './global-nav-product-recommendations/context/nav-product-recommendations-context';
import { useGlobalNavProductRecommendations } from './global-nav-product-recommendations/hooks/use-global-nav-product-recommendations';
import { useMore } from './hooks/use-more';
import { MobileGlobalNav } from './mobile-nav/mobile-nav';
import { CompoundSubNav, CompoundSubNavProps } from './nav-global-compound-subnav';
import GlobalNavLinksContainer, {
	GlobalNavLinkContentProps,
	GlobalNavProvider,
	SubnavLayout,
} from './nav-links-container';
import SearchAndProfile from './nav-search-and-profile';
import { ProductSubnav } from './product-subnav';
import { ResourcesSubnav } from './resources-subnav';
import SearchBar from './search-bar';
import { SkipToContent } from './skip-to-content';
import { SolutionsSubnav } from './solutions-subnav';
import { WhyAtlassianSubnav } from './why-atlassian-subnav';

export const GLOBAL_NAV_HEIGHT = '68px';

const navigationBarWrapperCss = css({
	alignItems: 'center',
	background: 'white',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	zIndex: 5,
	height: GLOBAL_NAV_HEIGHT,
	[media.below.md]: {
		display: 'none',
	},
});

const navigationBarLogoCss = css({
	display: 'flex',
	alignItems: 'center',
	paddingBlockStart: token('space.050'),
	[media.above.md]: {
		width: 'auto',
	},
});

const navigationBarLogoLink = css({
	display: 'flex',
});

const mobileNavCss = css({
	display: 'none',
	[media.below.md]: {
		display: 'block',
	},
});

const isStickyNavCss = css({
	position: 'sticky',
	insetBlockStart: 0,
});

export type GlobalNavProps = {
	productKey: ProductKeyTypes;
	isShowSearchBarAndProfileContainer?: boolean;
	isShowTryNowLink?: boolean;
	isShowSearchButton?: boolean;
	tryNowLinkCtaButtonColor?: ButtonColor;
	isNavSticky?: boolean;
	tryNowLinkCtaUrl?: string;
	compoundNav?: CompoundSubNavProps;
};

export type GlobalNavNavigationBarProps = GlobalNavProps & {
	globalLinksProps: GlobalNavLinkContentProps[];
};

export const GlobalNavNavigationBar = ({
	productKey,
	isShowSearchBarAndProfileContainer = true,
	isShowTryNowLink = false,
	isShowSearchButton = true,
	tryNowLinkCtaButtonColor,
	globalLinksProps,
	tryNowLinkCtaUrl,
}: GlobalNavNavigationBarProps) => {
	const intl = useIntl();
	const [isOpenSearch, setIsOpenSearch] = React.useState(false);
	const globalLinksRef = useRef<HTMLUListElement>(null);
	const searchAndProfileRef = useRef<HTMLDivElement>(null);
	const hasMounted = useRef(false);

	const {
		showMoreItems: setShowMore,
		shouldShowMoreItems: showMore,
		maxItems: maxLinks,
	} = useMore({
		leftElementRef: globalLinksRef,
		rightElementRef: searchAndProfileRef,
		initialMaxItems: globalLinksProps.length,
	});

	useEffect(() => {
		if (hasMounted.current) {
			const searchButton = document.getElementById('global-nav-search-bar-icon-desktop');
			if (searchButton) {
				searchButton.focus();
			}
		} else {
			hasMounted.current = true;
		}
	}, [isOpenSearch]);

	return (
		<div css={navigationBarWrapperCss}>
			<div
				css={css({
					display: 'none',
					alignItems: 'center',
					justifyContent: 'center',
					flex: 1,
					height: '100%',
					flexDirection: 'row',
					zIndex: 6,
					marginInline: '24px',

					[media.above.md]: {
						display: 'flex',
						maxWidth: '1152px',
						paddingInline: '0',
						marginInline: '60px',
					},
				})}
			>
				<nav
					aria-label={intl.formatMessage({
						id: 'wacGlobalNavigation.nav-label',
						defaultMessage: 'Main',
					})}
					css={css({
						display: 'flex',
						flexGrow: 1,
						flexShrink: 1,
						flexBasis: '0%',
						height: '100%',
						alignItems: 'center',
					})}
				>
					<div css={navigationBarLogoCss}>
						<SkipToContent />
						<a
							data-testid="navigation-bar-logo-link"
							href={useLocalizedHref('/')}
							aria-label={intl.formatMessage({
								id: 'wacGlobalNavigation.logo-label',
								defaultMessage: 'Atlassian logo',
							})}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container="globalNavWac"
							data-product-key={productKey}
							css={navigationBarLogoLink}
						>
							<LogoV3 brand="atlassian" type="logo" appearance="brand" width="187px" />
						</a>
					</div>
					{isOpenSearch ? (
						<SearchBar
							isOpenSearch={isOpenSearch}
							setIsOpenSearch={setIsOpenSearch}
							parentType="GlobalNav"
						/>
					) : (
						<GlobalNavLinksContainer
							globalLinksProps={globalLinksProps}
							globalLinksRef={globalLinksRef}
							maxLinks={maxLinks}
							showMore={showMore}
							setShowMore={setShowMore}
						/>
					)}
				</nav>
				{!isOpenSearch && isShowSearchBarAndProfileContainer && !showMore && (
					<FeatureExposed
						experimentKey="wac-globalui-team24-globalnav-cta"
						group="test"
						conditions={[!isShowTryNowLink]}
					>
						<SearchAndProfile
							isShowTryNowLink={isShowTryNowLink}
							// @ts-ignore
							ctaButtonColor={tryNowLinkCtaButtonColor}
							productKey={productKey}
							isShowSearchButton={isShowSearchButton}
							setIsOpenSearch={setIsOpenSearch}
							searchAndProfileRef={searchAndProfileRef}
							navigationTrynowCtaUrl={tryNowLinkCtaUrl}
						/>
					</FeatureExposed>
				)}
			</div>
		</div>
	);
};

export const GlobalNav = ({
	productKey = 'home',
	isShowSearchBarAndProfileContainer = true,
	isShowTryNowLink = false,
	isShowSearchButton = true,
	tryNowLinkCtaButtonColor,
	isNavSticky = true,
	tryNowLinkCtaUrl,
	compoundNav,
}: GlobalNavProps) => {
	const intl = useIntl();

	const enterpriseHref = useLocalizedHref('/enterprise');

	const globalLinksProps: GlobalNavLinkContentProps[] = useMemo(
		() => [
			{
				globalLinksKey: 'products',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.products',
					defaultMessage: 'Products',
				}),
				hasSubnav: true,
				content: <ProductSubnav productKey={productKey} />,
				layout: SubnavLayout.Product,
			},
			{
				globalLinksKey: 'solutions',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.solutions',
					defaultMessage: 'Solutions',
				}),
				hasSubnav: true,
				content: <SolutionsSubnav productKey={productKey} />,
				layout: SubnavLayout.Solutions,
			},
			{
				globalLinksKey: 'why-atlassian',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.whyAtlassian',
					defaultMessage: 'Why Atlassian',
				}),
				hasSubnav: true,
				content: <WhyAtlassianSubnav productKey={productKey} />,
				layout: SubnavLayout.WhyAtlassian,
			},
			{
				globalLinksKey: 'resources',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.resources',
					defaultMessage: 'Resources',
				}),
				hasSubnav: true,
				content: <ResourcesSubnav productKey={productKey} />,
				layout: SubnavLayout.Resources,
			},
			{
				globalLinksKey: 'enterprise',
				linkText: intl.formatMessage({
					id: 'globalNavigation.Navlink.enterprise',
					defaultMessage: 'Enterprise',
				}),
				hasSubnav: false,
				href: enterpriseHref,
			},
		],
		// Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[enterpriseHref, intl],
	);

	const globalNavItemKeys = useMemo(() => {
		return globalLinksProps.map((globalLink) => globalLink.globalLinksKey);
	}, [globalLinksProps]);

	const navProductRecosResponse = useGlobalNavProductRecommendations();

	return (
		<UFOSegment name="global-nav-v2">
			<div
				css={[
					css({
						position: 'relative',
						zIndex: '5',
						backgroundColor: wacToken('color.neutral.light0'),
					}),
					isNavSticky && isStickyNavCss,
				]}
			>
				<NavProductRecommendationsContext.Provider value={navProductRecosResponse}>
					<GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
						<GlobalNavNavigationBar
							productKey={productKey}
							isShowSearchBarAndProfileContainer={isShowSearchBarAndProfileContainer}
							isShowTryNowLink={isShowTryNowLink}
							tryNowLinkCtaUrl={tryNowLinkCtaUrl}
							isShowSearchButton={isShowSearchButton}
							tryNowLinkCtaButtonColor={tryNowLinkCtaButtonColor}
							globalLinksProps={globalLinksProps}
						/>
						<div css={mobileNavCss}>
							<MobileGlobalNav productKey={productKey} isShowSearchButton={isShowSearchButton} />
						</div>
						{compoundNav && <CompoundSubNav {...compoundNav} />}
					</GlobalNavProvider>
				</NavProductRecommendationsContext.Provider>
			</div>
		</UFOSegment>
	);
};

GlobalNav.displayName = 'GlobalNav';
